/**
 * Webhook Delete Confirmation
 *
 * Per Design 5.1: Soft-delete (deactivate) with warning that no more events
 * will be delivered. Can be re-enabled later.
 */

import { AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  Button,
} from '@/components/ui';

interface DeleteConfirmationProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  merchantId: string | null;
  onConfirm: () => void;
  isDeleting?: boolean;
}

export function DeleteConfirmation({
  open,
  onOpenChange,
  merchantId,
  onConfirm,
  isDeleting = false,
}: DeleteConfirmationProps) {
  const { t } = useTranslation('webhook-config');

  if (!merchantId) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <div className="flex items-center gap-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-destructive/10">
              <AlertTriangle className="h-5 w-5 text-destructive" />
            </div>
            <div>
              <DialogTitle>{t('delete.title')}</DialogTitle>
              <DialogDescription>{t('delete.warning')}</DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="py-4">
          <div className="rounded-md border bg-muted/50 p-4">
            <p className="font-mono text-sm text-muted-foreground">{merchantId}</p>
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isDeleting}>
            {t('common:button.cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button variant="destructive" onClick={onConfirm} disabled={isDeleting}>
            {isDeleting
              ? t('delete.deactivating', { defaultValue: 'Deactivating...' })
              : t('delete.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
